import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, Link } from 'react-router';
import { Mail, Lock, Eye, EyeOff, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { useAuth } from '@/hooks/useAuth';
import type { AxiosError } from 'axios';
import { AuthLayout } from './AuthLayout';
import { FormInput } from './FormInput';

/**
 * RegisterPage component that matches the Atlas Restoration Figma design
 * Features:
 * - Username, email, first name, last name, and password inputs with icons
 * - Show/hide password toggles
 * - Form validation (email format, password length, password match)
 * - Loading states
 * - Error handling
 * - Auto-login on successful registration
 * - Atlas brand colors (blue #0a0a9e, red #d71f20)
 */
export function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();

  // Form state
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!username.trim()) {
      setError('Username is required');
      return;
    }

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!firstName.trim()) {
      setError('First name is required');
      return;
    }

    if (!lastName.trim()) {
      setError('Last name is required');
      return;
    }

    if (!password) {
      setError('Password is required');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      // API expects fields with capital letters
      await register({
        Username: username,
        Email: email,
        Password: password,
        FirstName: firstName,
        LastName: lastName,
        RoleId: 3, // Default to User role
      });

      // Navigate to dashboard on success
      navigate('/');
    } catch (err) {
      // Handle different error types
      if (err instanceof Error) {
        setError(err.message);
      } else if ((err as AxiosError).response) {
        const axiosError = err as AxiosError<{ message?: string; errors?: string[] }>;
        const errorMessage =
          axiosError.response?.data?.errors?.join(', ') ||
          axiosError.response?.data?.message ||
          'Registration failed. Please try again.';
        setError(errorMessage);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Create Account" subtitle="Sign up to get started">
      <form onSubmit={handleSubmit} className="space-y-4">
        <ErrorAlert error={error} className="rounded-lg" />

        {/* Username input */}
        <FormInput
          id="username"
          label="Username"
          type="text"
          placeholder="Enter your username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          icon={<User className="h-[21px] w-[24px]" />}
          disabled={isSubmitting}
          required
        />

        {/* Email input */}
        <FormInput
          id="email"
          label="Email"
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          icon={<Mail className="h-[21px] w-[26px]" />}
          disabled={isSubmitting}
          required
        />

        {/* First Name input */}
        <FormInput
          id="firstName"
          label="First Name"
          type="text"
          placeholder="Enter your first name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          icon={<User className="h-[21px] w-[24px]" />}
          disabled={isSubmitting}
          required
        />

        {/* Last Name input */}
        <FormInput
          id="lastName"
          label="Last Name"
          type="text"
          placeholder="Enter your last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          icon={<User className="h-[21px] w-[24px]" />}
          disabled={isSubmitting}
          required
        />

        {/* Password input */}
        <FormInput
          id="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          placeholder="Enter your password (min 6 characters)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          icon={<Lock className="h-[21px] w-[24px]" />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400 hover:text-gray-600"
              tabIndex={-1}
              aria-label="Toggle password visibility"
            >
              {showPassword ? (
                <EyeOff className="h-[21px] w-[25px]" />
              ) : (
                <Eye className="h-[21px] w-[25px]" />
              )}
            </button>
          }
          disabled={isSubmitting}
          required
        />

        {/* Confirm Password input */}
        <FormInput
          id="confirmPassword"
          label="Confirm Password"
          type={showConfirmPassword ? 'text' : 'password'}
          placeholder="Confirm your password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          icon={<Lock className="h-[21px] w-[24px]" />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="text-gray-400 hover:text-gray-600"
              tabIndex={-1}
              aria-label="Toggle confirm password visibility"
            >
              {showConfirmPassword ? (
                <EyeOff className="h-[21px] w-[25px]" />
              ) : (
                <Eye className="h-[21px] w-[25px]" />
              )}
            </button>
          }
          disabled={isSubmitting}
          required
        />

        {/* Register button */}
        <Button
          type="submit"
          className="h-[49px] w-full rounded-[20px] text-[24px] font-semibold text-white hover:opacity-90"
          style={{ backgroundColor: '#d71f20' }}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating Account...' : 'Create Account'}
        </Button>

        {/* Sign in link */}
        <div className="text-center text-[16px]">
          <span className="font-medium text-black">Already have an account? </span>
          <Link
            to="/login"
            className="font-semibold underline hover:opacity-80"
            style={{ color: '#0a0a9e' }}
          >
            Sign in here
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
